import React, { useCallback, useRef, useState } from 'react'

import { logger } from '../../../../utils/logger'
import { validatePixKey } from '../../../../utils/validatePixType'
import { usePixKeyUpdate } from '../../hooks'
import { PixUpdateModal } from '../PixUpdateModal'

import {
  FormContainer,
  RadioGroup,
  Row,
  PixKeySubTitle,
  SelectInputWrapper,
  Select,
  Input,
  UpdateButton,
  Small
} from './styles'

const PixKeyForm = ({ cancellationId = null, onPixUpdated, cancellationData }) => {
  const [ownership, setOwnership] = useState('')
  const [pixType, setPixType] = useState('phone')
  const [recipient, setRecipient] = useState('')
  const [cpf, setCpf] = useState('')
  const [pixKey, setPixKey] = useState('')

  const isThirdParty = ownership === 'third'
  const modalRef = useRef()

  const { submitPixInfo, isLoading } = usePixKeyUpdate(cancellationId)

  const openModal = useCallback(() => modalRef.current.openModalFn(), [])
  const closeModal = useCallback(() => modalRef.current.closeModalFn(), [])

  const handleOwnershipChange = value => {
    setOwnership(value)
    if (value === 'own') {
      setRecipient(cancellationData?.cancellationRequest?.paymentInformation?.recipient)
      setCpf(cancellationData?.cancellationRequest?.paymentInformation?.CPF)
    } else {
      setRecipient('')
      setCpf('')
    }
    setPixType('phone')
    setPixKey('')
  }

  const handleConfirm = async () => {
    try {
      if (cancellationId) {
        await submitPixInfo({
          pixType,
          pixKey,
          recipient,
          CPF: cpf,
          thirdPartyRecipient: isThirdParty
        })
      }

      onPixUpdated({ recipient, CPF: cpf, pixKey, pixType, thirdPartyRecipient: isThirdParty })

      closeModal()
    } catch (error) {
      logger(error)
    }
  }

  const isCpfValid = /^\d{11}$/.test(cpf)
  const isPixKeyValid = validatePixKey(pixKey, pixType)

  const isButtonDisabledForThirdParty = !(ownership && recipient && isCpfValid && isPixKeyValid && pixType)
  const isButtonDisabledForOwn = !(ownership && isPixKeyValid && pixType)

  const isUpdateButtonDisabled = isThirdParty ? isButtonDisabledForThirdParty : isButtonDisabledForOwn
  const label = cancellationId ? 'Atualizar' : 'Confirmar'

  return (
    <FormContainer>
      <RadioGroup>
        <label>
          <input
            type="radio"
            name="ownership"
            value="own"
            checked={ownership === 'own'}
            onChange={() => handleOwnershipChange('own')}
          />
          Chave PIX<strong> vinculada a mim</strong>
        </label>
        <label>
          <input
            type="radio"
            name="ownership"
            value="third"
            checked={ownership === 'third'}
            onChange={() => handleOwnershipChange('third')}
          />
          Chave PIX <strong>vinculada a terceiros</strong>
        </label>
      </RadioGroup>

      {ownership && (
        <Row>
          {isThirdParty && (
            <Row>
              <Input
                value={recipient}
                onChange={e => setRecipient(e.target.value)}
                placeholder={isThirdParty ? 'Nome completo do terceiro' : 'Seu nome completo'}
              />

              <Input
                value={cpf}
                onChange={e => setCpf(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder="CPF do beneficiário (apenas números)"
                maxLength="11"
                pattern="[0-9]*"
              />
            </Row>
          )}

          <PixKeySubTitle>Chave Pix</PixKeySubTitle>

          <SelectInputWrapper>
            <Select value={pixType} onChange={e => setPixType(e.target.value)}>
              <option value="phone">Telefone</option>
              <option value="email">E-mail</option>
              <option value="cpf">CPF</option>
              <option value="random">Chave aleatória</option>
            </Select>
            <Input value={pixKey} onChange={e => setPixKey(e.target.value)} placeholder="Insira a chave PIX" />
          </SelectInputWrapper>

          {!isPixKeyValid && pixKey && <Small>Chave PIX inválida</Small>}
        </Row>
      )}

      <UpdateButton disabled={isUpdateButtonDisabled} onClick={openModal}>
        {isLoading ? 'Enviando...' : label}
      </UpdateButton>

      <PixUpdateModal
        pixInfo={{ recipient, CPF: cpf, pixKey, pixType }}
        thirdPartyRecipient={isThirdParty}
        cancellationData={cancellationData}
        onSubmit={handleConfirm}
        onClose={closeModal}
        modalRef={modalRef}
        isLoading={isLoading}
      />
    </FormContainer>
  )
}

export default PixKeyForm
